<template>
  <v-card color="grey lighten-4">
    <v-card-title
      class="text-uppercase font-weight-bold grey--text text--darken-3"
    >
      {{ $t("price_evolution") }}
      <v-spacer></v-spacer>
      <v-select
        v-model="selected_room_rate_id"
        :items="category_items"
        :label="$t('Categories')"
        item-text="text"
        item-value="value"
        @change="onCategoryChange"
        rounded
        outlined
        dense
        background-color="white"
        hide-details
        clearable
        class="font-weight-regular text-capitalize"
      >
      </v-select>
    </v-card-title>
    <v-card-text
      class="white pa-0"
      style="position: relative; min-height: 520px;"
    >
      <v-overlay :value="chart_loading" absolute opacity="0.08">
        <v-progress-circular indeterminate size="48" color="primary" />
      </v-overlay>
      <a-rms-day-price-evolution-chart
        v-if="chart_data"
        :current_price="chart_data.current_price"
        :series="chart_data"
        height="520px"
      />
    </v-card-text>
    <v-card-text class="white">
      <v-data-table
        :headers="headers"
        :items="sortedRatesForTable"
        hide-default-footer
        disable-pagination
        disable-sort
        :item-class="getDatatableRowClass"
        :loading="loading"
      >
        <template v-slot:[`item.date`]="{ item }">
          {{ getFriendlyDate(item.date) }}
        </template>
        <template v-slot:[`item.change_hour`]="{ item }">
          {{ formatTimeFromRowDate(item) }}
        </template>
        <template v-slot:[`item.modified_user`]="{ item }">
          {{ rowModifiedUser(item) }}
        </template>
        <template v-slot:[`item.pvp`]="{ item }">
          <a-kpi
            kpi="pvp"
            :value="item.pvp"
            :vs_values="[item.pvp_vs]"
            :vs_periods="['old_rate']"
            :absolute="true"
            x_small
          ></a-kpi>
        </template>
        <template v-slot:[`item.roomtype`]="{ item }">
          {{ item.roomtype }}
        </template>
        <template v-slot:[`item.rn_day`]="{ item }">
          <template v-if="item.rn_day > 0">
            {{ item.rn_day + " " + $t("room_day") }}
          </template>
        </template>
        <template v-slot:[`item.rn`]="{ item }">
          {{ item.rn }}
        </template>
        <template v-slot:[`item.days`]="{ item }">
          {{ item.days }}
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import Vue from "vue";
import { mapState, mapGetters } from "vuex";
import * as Utils from "@/utils.js";
import aKpi from "@/components/rms_ui/aKpi.vue";
import aRmsDayPriceEvolutionChart from "@/components/chart/aRmsDayPriceEvolutionChart.vue";

export default {
  name: "a-rms-day-price-evolution",
  components: {
    aKpi,
    aRmsDayPriceEvolutionChart
  },
  props: {
    date: String
  },
  data: () => ({
    selected_room_rate_id: null,
    rates: [],
    loading: false,
    chart_loading: false,
    chart_data: null,
    categorySeeded: false
  }),
  methods: {
    getDatatableRowClass() {
      return "a-datatable-row";
    },
    applyCategoryDefaultFromHighlight() {
      const types = this.roomtype_types_mapped;
      if (!types || !types.length) return;
      const h = types.find(t => Number(t.highlighted) === 1);
      this.selected_room_rate_id = h ? h.id : null;
    },
    rowSortByEndpointDate(row) {
      if (!row || !row.date) return 0;
      const n = new Date(row.date).getTime();
      if (!isNaN(n)) return n;
      const m = Vue.moment(row.date);
      return m.isValid() ? m.valueOf() : 0;
    },
    formatTimeFromRowDate(item) {
      if (!item || !item.date) return "";
      const m = Vue.moment(item.date);
      return m.isValid() ? m.format("HH:mm") : "";
    },
    rowModifiedUser(item) {
      return (
        item.modified_by_name ||
        item.modified_by ||
        item.user_name ||
        item.user ||
        item.username ||
        item.modified_user ||
        ""
      );
    },
    evolutionPayload() {
      const payload = { date: this.date };
      if (this.selected_room_rate_id != null) {
        payload.room_rate_id = this.selected_room_rate_id;
      }
      return payload;
    },
    async getRates() {
      this.loading = true;
      await this.$store
        .dispatch("fetchRmsRoomTypesEvolutionDay", this.evolutionPayload())
        .then(response => {
          this.rates = response;
          this.loading = false;
        })
        .catch(() => {
          Utils.showError(this, "something_went_wrong");
          this.loading = false;
        });
    },
    getFriendlyDate(date) {
      return Utils.getFriendlyDate(date, true);
    },
    getChartData() {
      this.chart_loading = true;
      this.$store
        .dispatch("fetchRmsRoomTypesEvolutionDayGraph", this.evolutionPayload())
        .then(response => {
          this.chart_data = response;
        })
        .catch(() => {})
        .finally(() => {
          this.chart_loading = false;
        });
    },
    onCategoryChange() {
      this.getRates();
      this.getChartData();
    },
    ensureRoomTypesLoaded() {
      if (
        !this.roomtype_types_mapped ||
        this.roomtype_types_mapped.length === 0
      ) {
        this.$store.dispatch("fetchRoomtypeTypesMapped", {});
      }
    },
    refreshAll() {
      this.getRates();
      this.getChartData();
    },
    seedCategoryAndRefreshIfNeeded() {
      if (!this.roomtype_types_mapped || !this.roomtype_types_mapped.length) {
        return;
      }
      if (!this.current_hotel || !this.current_hotel.id) return;
      if (!this.categorySeeded) {
        this.applyCategoryDefaultFromHighlight();
        this.categorySeeded = true;
        this.refreshAll();
      }
    }
  },
  computed: {
    sortedRatesForTable() {
      return [...this.filteredRates].sort(
        (a, b) => this.rowSortByEndpointDate(b) - this.rowSortByEndpointDate(a)
      );
    },
    category_items() {
      const list = (this.roomtype_types_mapped || []).slice().sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), undefined, {
          sensitivity: "base"
        })
      );
      return [
        { text: this.$t("all_categories"), value: null },
        ...list.map(rt => ({ text: rt.name, value: rt.id }))
      ];
    },
    filteredRates() {
      if (this.rates.length === 0) {
        return [];
      }
      if (this.selected_room_rate_id == null) {
        return this.rates;
      }
      const hasRateId = this.rates.some(r => r.room_rate_id != null);
      if (hasRateId) {
        return this.rates.filter(
          r => r.room_rate_id === this.selected_room_rate_id
        );
      }
      return this.rates;
    },
    headers() {
      return [
        {
          text: this.$t("date"),
          align: "start",
          sortable: false,
          value: "date"
        },
        {
          text: this.$t("table_column_time"),
          align: "center",
          sortable: false,
          value: "change_hour"
        },
        {
          text: this.$t("user"),
          align: "center",
          sortable: false,
          value: "modified_user"
        },
        {
          text: this.$t("pvp"),
          align: "center",
          sortable: false,
          value: "pvp"
        },
        {
          text: this.$t("roomtype"),
          align: "center",
          sortable: false,
          value: "roomtype"
        },
        {
          text: this.$t("daily_average"),
          align: "center",
          sortable: false,
          value: "rn_day"
        },
        {
          text: this.$t("rn"),
          align: "center",
          sortable: false,
          value: "rn"
        },
        {
          text: this.$t("days"),
          align: "center",
          sortable: false,
          value: "days"
        }
      ];
    },
    ...mapState({
      roomtype_types_mapped: state => state.roomtype.types_mapped
    }),
    ...mapGetters({
      current_hotel: "current_hotel"
    })
  },
  watch: {
    roomtype_types_mapped: {
      handler() {
        this.seedCategoryAndRefreshIfNeeded();
      },
      immediate: true
    },
    current_hotel(n, p) {
      if (n && p && n.id !== p.id) {
        this.categorySeeded = false;
        this.$nextTick(() => this.seedCategoryAndRefreshIfNeeded());
      }
    },
    date() {
      this.refreshAll();
    }
  },
  mounted() {
    this.ensureRoomTypesLoaded();
  }
};
</script>
